import {
  BYTES_IN_DECIMAL_GIGABYTE,
  BYTES_IN_DECIMAL_KILOBYTE,
  BYTES_IN_DECIMAL_MEGABYTE,
  BYTES_IN_GIGABYTE,
  BYTES_IN_KILOBYTE,
  BYTES_IN_MEGABYTE,
  MAX_AUDIO_UPLOAD_SIZE_BYTES,
  MAX_IN_FLIGHT_UPLOAD_PROGRESS,
  MAX_VIDEO_UPLOAD_SIZE_BYTES,
  S3_CHECKSUM_SHA256_HEADER,
  UPLOAD_STALL_TIMEOUT_MS,
  UPLOAD_STALL_TIMEOUT_SECONDS,
  UPLOAD_STEP_I18N_KEY,
  VALID_UPLOAD_MIME_TYPES,
} from "../constants/uploads";
import type {
  ICountUploadsInFlightParams,
  ICreateUploadProgressReporterProps,
  IFormatSizeLadderProps,
  IFormatUploadSizeLabelParams,
  IPresignAndUploadFileProps,
  IPresignAndUploadFileResult,
  IResolveUploadedMeetingCompletionDateParams,
  IResolveUploadMaxSizeBytesParams,
  IResolveUploadProgressLabelParams,
  IResolveUploadRowActionsParams,
  IResolveUploadStepDescriptionParams,
  IUploadedMeetingCompletionDate,
  IUploadFileToS3Props,
  IUploadProgressLabel,
  IUploadProgressReporterEvent,
  IUploadRowActions,
  IUploadStepDescription,
  IValidateFileResult,
  IValidateUploadFileParams,
} from "../types/uploads";
import { EFileUploadStatus, EUploadFailureReason } from "../types/uploads";
import { createAbortError } from "./abort-error";
import { createUploadFailureError } from "./upload-error";

const validUploadMimeTypeSet = new Set<string>(VALID_UPLOAD_MIME_TYPES);

// NOTE: The meeting uploader's size cap, which depends on the format: video is
// allowed six times what audio is. Extracted from `validateUploadFile` for
// NSAI-5339 so the integration picker can apply the very same limit to a file
// pulled out of Drive/OneDrive/Dropbox. Before that, the picker fell back to the
// chat attachment cap (50 MB) and refused a recording the dropzone would have
// taken — the two routes to the same destination disagreed by a factor of 30.
export const resolveUploadMaxSizeBytes = ({
  mimeType,
}: IResolveUploadMaxSizeBytesParams): number =>
  mimeType.startsWith("video/")
    ? MAX_VIDEO_UPLOAD_SIZE_BYTES
    : MAX_AUDIO_UPLOAD_SIZE_BYTES;

// NOTE: NSAI-4891 — the rejection used to be the bare sentence "File is too
// large.", which named neither the cap nor the file, leaving the user to guess
// how much smaller "smaller" had to be while the dropzone two lines above
// already stated both limits. The caps also differ by format (audio 250 MB,
// video 1.5 GB), so a single hardcoded number in the copy would be wrong half
// the time — the resolved limit travels with the error instead.
//
// NOTE: The two labels come from different ladders on purpose, per NSAI-4790:
// the cap is binary (`formatUploadLimitLabel`, so it still reads the promised
// "250 MB" / "1.5 GB") and the file's own size is decimal
// (`formatUploadSizeLabel`, so it matches what the OS told the user). That is
// also what keeps the sentence true at the boundary: a file one byte over the
// audio cap reads "262.1 MB", never "250 MB", so the size can never round onto
// the limit it just exceeded.
export const validateUploadFile = ({
  mimeType,
  size,
}: IValidateUploadFileParams): IValidateFileResult => {
  if (!validUploadMimeTypeSet.has(mimeType)) {
    return { isValid: false, errorKey: "error.invalid_file_type" };
  }

  const maxSizeBytes = resolveUploadMaxSizeBytes({ mimeType });
  const max = formatUploadLimitLabel({ sizeInBytes: maxSizeBytes });

  // NOTE: Reject files if size is unknown to prevent uploading oversized files.
  // Its own key: an unreadable size is not the same failure as an oversized
  // file, and saying "too large" about a file we never measured is a guess the
  // user cannot act on. We can still name the limit it would have been held to.
  if (size === null) {
    return {
      isValid: false,
      errorKey: "error.file_size_unknown",
      errorParams: { max },
    };
  }

  if (size > maxSizeBytes) {
    return {
      isValid: false,
      errorKey: "error.file_too_large",
      errorParams: {
        max,
        size: formatUploadSizeLabel({ sizeInBytes: size }),
      },
    };
  }

  return { isValid: true };
};

// NOTE: Round to at most 1 decimal so arbitrary sizes (e.g. a 327.83 MB
// recording) read cleanly. `Number(x.toFixed(1))` drops a trailing ".0", so a
// value that lands on a whole unit still renders as "250 MB" / "1.5 GB".
//
// NOTE: NSAI-3309 — below a megabyte this ladder used to round everything to
// "0 MB", so every file on a skill's detail screen claimed to be empty: a
// 75-byte script and a 2 KB note read identically and carried no information at
// all. Skill resources are routinely small, which is why it showed up there
// first, but the formatter is shared with uploads and meetings and was wrong for
// all of them — hence the KB and B rungs.
//
// NOTE: NSAI-4790 — each tier is rounded *before* it is accepted, because the
// rounding can push a value out of the tier that selected it. 999,950 bytes is
// below a megabyte, so the KB rung claims it — but `(999950 / 1000).toFixed(1)`
// is 1000, and "1000 KB" is not a reading decimal notation uses. Comparing the
// rounded value back against the tier's ceiling promotes those last few bytes to
// "1 MB" instead. The binary ladder has the same edge one rung up (1,048,575 B
// would read "1024 KB"), so the check lives here rather than in either caller.
const roundToOneDecimal = (value: number): number => Number(value.toFixed(1));

const formatSizeLadder = ({
  sizeInBytes,
  bytesInKilobyte,
  bytesInMegabyte,
  bytesInGigabyte,
}: IFormatSizeLadderProps): string => {
  if (sizeInBytes >= bytesInGigabyte) {
    return `${roundToOneDecimal(sizeInBytes / bytesInGigabyte)} GB`;
  }

  if (sizeInBytes >= bytesInMegabyte) {
    const megabytes = roundToOneDecimal(sizeInBytes / bytesInMegabyte);

    return megabytes * bytesInMegabyte >= bytesInGigabyte
      ? `${roundToOneDecimal(sizeInBytes / bytesInGigabyte)} GB`
      : `${megabytes} MB`;
  }

  if (sizeInBytes >= bytesInKilobyte) {
    const kilobytes = roundToOneDecimal(sizeInBytes / bytesInKilobyte);

    return kilobytes * bytesInKilobyte >= bytesInMegabyte
      ? `${roundToOneDecimal(sizeInBytes / bytesInMegabyte)} MB`
      : `${kilobytes} KB`;
  }

  return `${sizeInBytes} B`;
};

// NOTE: NSAI-4790 — the size of a real file, in decimal (SI) units, because that
// is what every OS this app runs next to reports. Dividing by 1024 under a "KB"
// label printed KiB values wearing decimal units: a 39,724-byte attachment read
// "38.8 KB" on its chip while the iOS share sheet, macOS Finder and the Android
// Files app all said "40 KB" for the same bytes. Use this for bytes that came
// from a file; for an upload cap use `formatUploadLimitLabel` instead.
export const formatUploadSizeLabel = ({
  sizeInBytes,
}: IFormatUploadSizeLabelParams): string => {
  return formatSizeLadder({
    sizeInBytes,
    bytesInKilobyte: BYTES_IN_DECIMAL_KILOBYTE,
    bytesInMegabyte: BYTES_IN_DECIMAL_MEGABYTE,
    bytesInGigabyte: BYTES_IN_DECIMAL_GIGABYTE,
  });
};

// NOTE: NSAI-4790 — an upload *cap*, which is a different question from the size
// of a file and deliberately keeps binary divisors. The caps are defined in
// binary (MAX_AUDIO_UPLOAD_SIZE_BYTES = 250 * 1024 * 1024) and the server
// enforces those exact byte counts, so dividing them decimally would advertise
// "262.1 MB" and "1.6 GB" where the copy has always promised "250 MB" and
// "1.5 GB". Both readings describe the same true limit; this one is the round
// number a user can act on. Making the caps themselves decimal would be a real
// change to what the product accepts, and belongs to product, not to this fix.
export const formatUploadLimitLabel = ({
  sizeInBytes,
}: IFormatUploadSizeLabelParams): string => {
  return formatSizeLadder({
    sizeInBytes,
    bytesInKilobyte: BYTES_IN_KILOBYTE,
    bytesInMegabyte: BYTES_IN_MEGABYTE,
    bytesInGigabyte: BYTES_IN_GIGABYTE,
  });
};

// NOTE: NSAI-5132 — decides what the right edge of an Uploads progress row
// reads, shared so web and mobile say the same thing about the same state.
// The interesting case is FETCHING: a provider stream that carries a
// Content-Length yields a percentage, one that does not yields only the bytes
// received so far, and `progress === 100` means the bytes are all in and the
// client is turning them into a file — work the user still waits on, so the
// row must not sit at a silent 100%.
export const resolveUploadProgressLabel = ({
  status,
  progress,
  downloadedBytes,
}: IResolveUploadProgressLabelParams): IUploadProgressLabel => {
  if (status === EFileUploadStatus.CONFIRMING) {
    return { i18nKey: "table.processing_badge" };
  }

  if (status !== EFileUploadStatus.FETCHING) {
    return { percent: progress };
  }

  if (progress >= 100) {
    return { i18nKey: "progress.preparing" };
  }

  if (progress > 0) {
    return { percent: progress };
  }

  if (downloadedBytes !== undefined && downloadedBytes > 0) {
    return { sizeInBytes: downloadedBytes };
  }

  return { i18nKey: "progress.fetching" };
};

// NOTE: NSAI-5561 — the caption under a progress row, naming the stage the file
// is in: being fetched from the provider, uploading, being handed over for
// transcription, done. The bar label beside it carries the number (%, bytes);
// this says what that number is about. ERROR returns null because the row
// shows the server's reason there instead, and CANCELLED because an aborted
// row is removed and toasted rather than rendered. Shared so web and mobile
// name every step the same way.
export const resolveUploadStepDescription = ({
  status,
  sourceName,
}: IResolveUploadStepDescriptionParams): IUploadStepDescription | null => {
  if (status === EFileUploadStatus.FETCHING) {
    return sourceName
      ? { i18nKey: "steps.fetching_from", source: sourceName }
      : { i18nKey: "steps.fetching" };
  }

  if (
    status === EFileUploadStatus.ERROR ||
    status === EFileUploadStatus.CANCELLED
  ) {
    return null;
  }

  return { i18nKey: UPLOAD_STEP_I18N_KEY[status] };
};

// NOTE: NSAI-5132 — which of a progress row's affordances apply in a given
// state. Shared so the two clients cannot disagree about when the row's X
// aborts a transfer and when it merely dismisses a finished entry — a
// disagreement that would let one platform offer "cancel" on a row whose
// upload is already done.
export const resolveUploadRowActions = ({
  status,
}: IResolveUploadRowActionsParams): IUploadRowActions => {
  const isFetching = status === EFileUploadStatus.FETCHING;

  return {
    isFetching,
    isCancellable: isFetching || status === EFileUploadStatus.UPLOADING,
    isDismissable:
      status === EFileUploadStatus.ERROR || status === EFileUploadStatus.DONE,
  };
};

// NOTE: NSAI-4874 — the only place a raw S3 PUT `progress` event becomes a
// percentage. The event cannot be trusted as-is: Android over-reports `loaded`
// (see MAX_IN_FLIGHT_UPLOAD_PROGRESS), okhttp restarts its counter on a retry,
// and a total of 0 has no ratio at all. So: skip junk, clamp, only move
// forward, and drop repeats. Upload twin of `createDownloadProgressReporter`.
export const createUploadProgressReporter = ({
  onProgress,
}: ICreateUploadProgressReporterProps): ((
  event: IUploadProgressReporterEvent,
) => void) => {
  let lastReportedProgress = 0;

  return (event: IUploadProgressReporterEvent): void => {
    if (
      !Number.isFinite(event.loaded) ||
      !Number.isFinite(event.total) ||
      event.total <= 0
    ) {
      return;
    }

    const percent = Math.min(
      MAX_IN_FLIGHT_UPLOAD_PROGRESS,
      Math.max(0, Math.round((event.loaded / event.total) * 100)),
    );

    if (percent <= lastReportedProgress) {
      return;
    }

    lastReportedProgress = percent;
    onProgress(percent);
  };
};

// NOTE: Uploads a file to S3 via presigned PUT URL with progress tracking.
// Uses XMLHttpRequest instead of fetch() because fetch does not support upload progress events.
export const uploadFileToS3 = ({
  uploadUrl,
  body,
  contentType,
  onProgress,
  signal,
  hasUploadProgressEvents = true,
  checksumSha256,
}: IUploadFileToS3Props): Promise<void> =>
  new Promise((resolve, reject) => {
    // NOTE: Reject immediately if already aborted before starting XHR
    if (signal?.aborted) {
      reject(createAbortError("Upload cancelled"));
      return;
    }

    const xhr = new XMLHttpRequest();
    xhr.open("PUT", uploadUrl);
    xhr.setRequestHeader("Content-Type", contentType);
    // NOTE: NSAI-6953 — only for a caller that declared this digest at
    // presign; see IUploadFileToS3Props.checksumSha256.
    if (checksumSha256 !== undefined) {
      xhr.setRequestHeader(S3_CHECKSUM_SHA256_HEADER, checksumSha256);
    }

    let stallTimer: ReturnType<typeof setTimeout> | null = null;

    const clearStallTimer = (): void => {
      if (stallTimer !== null) {
        clearTimeout(stallTimer);
        stallTimer = null;
      }
    };

    // NOTE: NSAI-5139 — this guard measures "progress stopped", so it needs a
    // progress event to reset it. iOS emits none, so arming it on send killed
    // healthy iOS uploads at 60 s (150 MB file, 40 MB sent, sim-bridge req-103).
    // Platforms that do report progress still arm on send as before; only iOS
    // passes `hasUploadProgressEvents: false` and waits for the first event.
    const resetStallTimer = (): void => {
      clearStallTimer();
      stallTimer = setTimeout(() => {
        // NOTE: reject before abort — xhr.abort() fires the abort event synchronously,
        // which would otherwise settle the promise with AbortError first and swallow
        // the stall message (AbortError is treated as a silent user cancellation).
        reject(
          createUploadFailureError({
            reason: EUploadFailureReason.STALLED,
            // NOTE: NSAI-4883 — states what the client observed and stops there.
            // The advice this used to carry ("check your connection") reaches
            // the user through `describeErrorForDisplay`'s "View details", and
            // NSAI-5139 is proof it can be wrong: on iOS the guard fires while
            // the transfer is healthy.
            message: `Upload stalled — no progress for ${UPLOAD_STALL_TIMEOUT_SECONDS} seconds.`,
            params: { seconds: UPLOAD_STALL_TIMEOUT_SECONDS },
          }),
        );
        xhr.abort();
      }, UPLOAD_STALL_TIMEOUT_MS);
    };

    // NOTE: NSAI-5139 — attached even when the caller wants no percentages,
    // because this listener is what arms and resets the stall guard above.
    const reportProgress = onProgress
      ? createUploadProgressReporter({ onProgress })
      : null;

    xhr.upload.addEventListener("progress", (event) => {
      resetStallTimer();
      if (reportProgress !== null && event.lengthComputable) {
        reportProgress({ loaded: event.loaded, total: event.total });
      }
    });

    xhr.addEventListener("load", () => {
      clearStallTimer();
      if (xhr.status >= 200 && xhr.status < 300) {
        // NOTE: NSAI-4874 — the only writer of 100%, so a full bar means the
        // PUT finished rather than a byte counter reaching the end.
        onProgress?.(100);
        resolve();
      } else {
        reject(
          createUploadFailureError({
            reason: EUploadFailureReason.REJECTED,
            message: `S3 upload failed with status ${xhr.status}`,
            params: { status: xhr.status },
          }),
        );
      }
    });

    xhr.addEventListener("error", () => {
      clearStallTimer();
      reject(
        createUploadFailureError({
          reason: EUploadFailureReason.NETWORK,
          message: "S3 upload network error",
        }),
      );
    });

    xhr.addEventListener("abort", () => {
      clearStallTimer();
      reject(createAbortError("Upload cancelled"));
    });

    if (signal) {
      signal.addEventListener("abort", () => xhr.abort(), { once: true });
    }

    // NOTE: A platform with no progress events would never reset this, so it
    // only starts here when the caller says progress is coming.
    if (hasUploadProgressEvents) {
      resetStallTimer();
    }

    xhr.send(body);
  });

// NOTE: Single-call helper that presigns a PUT URL for the caller's domain (chat,
// assistants, probes, …) and then streams the blob to S3. Centralising the two-step
// pipeline keeps web and mobile upload flows identical — both pass a domain-specific
// `presign` callback and the blob/URI payload they already hold.
export const presignAndUploadFile = async ({
  presign,
  fileName,
  contentType,
  fileSizeBytes,
  body,
  onProgress,
  onPresignComplete,
  signal,
  hasUploadProgressEvents,
  checksumSha256,
}: IPresignAndUploadFileProps): Promise<IPresignAndUploadFileResult> => {
  const presignResult = await presign({
    fileName,
    contentType,
    ...(fileSizeBytes !== undefined && { fileSizeBytes }),
    ...(signal !== undefined && { signal }),
    ...(checksumSha256 !== undefined && { checksumSha256 }),
  });

  // NOTE: Notify the caller with the s3Key BEFORE the PUT starts so they can
  // stash it on local state. If the PUT is later aborted / fails, the caller
  // can still find the key to fire a server-side release for cleanup.
  onPresignComplete?.(presignResult);

  await uploadFileToS3({
    uploadUrl: presignResult.uploadUrl,
    body,
    contentType,
    ...(onProgress !== undefined && { onProgress }),
    ...(signal !== undefined && { signal }),
    ...(hasUploadProgressEvents !== undefined && { hasUploadProgressEvents }),
    ...(checksumSha256 !== undefined && { checksumSha256 }),
  });

  return { s3Key: presignResult.s3Key };
};

// NOTE: NSAI-5144 — the Uploads list badge answers "when was this recording
// finished?", so it prefers the processor's completion timestamp. `processedAt`
// is null for meetings that never completed a run and for rows created before
// the column existed; those fall back to the upload date, which is what the
// badge showed before this change. The caller uses `isProcessed` to label the
// value honestly instead of calling an upload date a processing date.
export const resolveUploadedMeetingCompletionDate = ({
  processedAt,
  createdAt,
}: IResolveUploadedMeetingCompletionDateParams): IUploadedMeetingCompletionDate =>
  processedAt
    ? { dateString: processedAt, isProcessed: true }
    : { dateString: createdAt, isProcessed: false };

// NOTE: NSAI-5241 — how many transfers a navigation badge should announce.
// Only UPLOADING and CONFIRMING count as in flight: a DONE or ERROR row stays
// in the list until the user dismisses it, so counting the list length would
// pin a badge to the sidebar long after the last byte moved. Shared between
// the web sidebar badge and the mobile one so the two can never drift apart on
// what "in progress" means.
export const countUploadsInFlight = ({
  uploads,
}: ICountUploadsInFlightParams): number =>
  uploads.filter(
    (entry) =>
      entry.status === EFileUploadStatus.UPLOADING ||
      entry.status === EFileUploadStatus.CONFIRMING,
  ).length;
